<template>
    <div class="grid items-center grid-cols-3 gap-2">
        <div>
            <Field
                v-model="intervalType"
                label-position="none"
                :options="[
                    {
                        id: 'daily',
                        name: lang('fields::fields.date-relative.options.daily'),
                    },
                    {
                        id: 'weekly',
                        name: lang('fields::fields.date-relative.options.weekly'),
                    },
                    {
                        id: 'monthly',
                        name: lang('fields::fields.date-relative.options.monthly'),
                    },
                    {
                        id: 'customDays',
                        name: lang('fields::fields.date-relative.options.customDays'),
                    },
                    {
                        id: 'customWeeks',
                        name: lang('fields::fields.date-relative.options.customWeeks'),
                    },
                    {
                        id: 'customMonths',
                        name: lang('fields::fields.date-relative.options.customMonths'),
                    },
                    {
                        id: 'nthDayMonth',
                        name: lang('fields::fields.date-relative.options.nthDayMonth'),
                    },
                ]"
                :field="{
                    type: 'select',
                    adminComponent: 'AdminSelect',
                }"
                :dusk="`${props.dusk}-interval`"
                :size="props.size"
                :disabled="props.disabled"
            />
        </div>

        <div v-if="intervalType === 'customDays' || intervalType === 'customWeeks' || intervalType === 'customMonths'">
            <Field
                v-model="countValue"
                label-position="none"
                :field="{
                    type: 'number',
                    adminComponent: 'AdminNumber',
                }"
                :dusk="`${props.dusk}-count`"
                :size="props.size"
                :disabled="props.disabled"
            />
        </div>

        <div v-if="intervalType === 'nthDayMonth'">
            <Field
                v-model="nthDayValue"
                label-position="none"
                :field="{
                    type: 'number',
                    adminComponent: 'AdminNumber',
                }"
                :dusk="`${props.dusk}-nth`"
                :size="props.size"
                :disabled="props.disabled"
            />
        </div>

        <div class="text-sm">
            <strong>{{ composedLang }}</strong>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { lang } from '@/lang/composable';

const props = withDefaults(defineProps<FieldComponent>(), {});
const modelValue = defineModel({
    type: String,
    default: '',
});

const intervalType = ref<
    'daily' | 'weekly' | 'monthly' | 'customDays' | 'customWeeks' | 'customMonths' | 'nthDayMonth'
>('daily');

const countValue = ref<number>(1);

const nthDayValue = ref<number>(1);

const composedValue = computed<string>(() => {
    switch (intervalType.value) {
        case 'daily':
            return 'every day';
        case 'weekly':
            return 'every week';
        case 'monthly':
            return 'every month';
        case 'customDays':
            return `every ${countValue.value} days`;
        case 'customWeeks':
            return `every ${countValue.value} weeks`;
        case 'customMonths':
            return `every ${countValue.value} months`;
        case 'nthDayMonth':
            return `every ${nthDayValue.value} day of the month`;
        default:
            return '';
    }
});

const composedLang = computed<string>(() => {
    switch (intervalType.value) {
        case 'daily':
            return lang('fields::fields.date-relative.human.daily');
        case 'weekly':
            return lang('fields::fields.date-relative.human.weekly');
        case 'monthly':
            return lang('fields::fields.date-relative.human.monthly');
        case 'customDays':
            return lang('fields::fields.date-relative.human.customDays', { days: countValue.value });
        case 'customWeeks':
            return lang('fields::fields.date-relative.human.customWeeks', { weeks: countValue.value });
        case 'customMonths':
            return lang('fields::fields.date-relative.human.customMonths', { months: countValue.value });
        case 'nthDayMonth':
            return lang('fields::fields.date-relative.human.nthDayMonth', { nth: nthDayValue.value });
        default:
            return '';
    }
});

watch(composedValue, (newVal) => {
    modelValue.value = newVal;
});

watch(
    () => modelValue.value,
    (val) => {
        if (!val) {
            intervalType.value = 'daily';
            countValue.value = 1;
            nthDayValue.value = 1;
            return;
        }

        if (val === 'every day') {
            intervalType.value = 'daily';
        } else if (val === 'every week') {
            intervalType.value = 'weekly';
        } else if (val === 'every month') {
            intervalType.value = 'monthly';
        } else if (/^every (\d+) days?$/.test(val)) {
            intervalType.value = 'customDays';
            countValue.value = parseInt(RegExp.$1, 10);
        } else if (/^every (\d+) weeks?$/.test(val)) {
            intervalType.value = 'customWeeks';
            countValue.value = parseInt(RegExp.$1, 10);
        } else if (/^every (\d+) months?$/.test(val)) {
            intervalType.value = 'customMonths';
            countValue.value = parseInt(RegExp.$1, 10);
        } else if (/^every (\d+) day of the month$/.test(val)) {
            intervalType.value = 'nthDayMonth';
            nthDayValue.value = parseInt(RegExp.$1, 10);
        }
    },
    { immediate: true },
);
</script>
